import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GRAHA_DETAILS } from "../content/grahas";

/**
 * Sliding navagraha carousel (replaces the icon grid on the home page).
 *
 * Left: a single square art canvas that cross-fades between the nine
 * Pattachitra-style illustrations, with the active graha's caption pinned
 * at the bottom.
 * Right: the nine graha dots — clickable, showing Devanagari + English name.
 * Auto-advances every 5.2s; hover pauses it; each dot is `aria-current` when
 * active. The canvas itself is a link to the per-graha detail page.
 */

const AUTO_MS = 5200;

export function GrahaCarousel({ testId = "graha-carousel" }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = window.setTimeout(
      () => setI((v) => (v + 1) % GRAHA_DETAILS.length),
      AUTO_MS,
    );
    return () => window.clearTimeout(timer.current);
  }, [i, paused]);

  const active = GRAHA_DETAILS[i];

  return (
    <div
      className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={testId}
    >
      <Link
        to={`/graha/${active.slug}`}
        className="graha-canvas group"
        aria-label={`${active.graha} — ${active.planet}. Open detail page.`}
        data-testid={`${testId}-canvas`}
      >
        {GRAHA_DETAILS.map((g, idx) => (
          <img
            key={g.slug}
            src={`/img/graha/${g.slug}.webp`}
            alt=""
            aria-hidden="true"
            loading={idx === 0 ? "eager" : "lazy"}
            decoding="async"
            data-visible={idx === i ? "true" : "false"}
            className="graha-canvas__img"
          />
        ))}
        <div className="graha-canvas__scrim" />
        <div className="graha-canvas__caption">
          <p className="graha-canvas__cap-eyebrow">{active.day} · {active.direction}</p>
          <p className="graha-canvas__cap-deva">{active.deva}</p>
          <p className="graha-canvas__cap-name">
            {active.graha} <span className="text-white/60">— {active.planet}</span>
          </p>
          <p className="ui mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-[color:#f0d693] transition-transform group-hover:translate-x-1">
            Read the graha <ArrowRight className="h-3.5 w-3.5" />
          </p>
        </div>
      </Link>

      <div className="flex flex-col justify-center">
        <p className="eyebrow">Browse by graha</p>
        <h3 className="mt-2 text-[28px] leading-[1.15] tracking-[-0.005em]"
            style={{ fontFamily: "Fraunces, Georgia, serif" }}>
          The nine grahas and their principal stones
        </h3>
        <p className="mt-3 max-w-[var(--measure-read)] text-[15px] leading-[1.7] text-dim"
           style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
          Each graha carries a principal stone in the classical scheme and, for
          most, one or more rudraksha counts. Tap a graha to read what the
          tradition actually says — and to see the certified pieces we hold under it.
        </p>

        <div className="graha-dots mt-6" data-testid={`${testId}-dots`}>
          {GRAHA_DETAILS.map((g, idx) => (
            <button
              key={g.slug}
              type="button"
              onClick={() => setI(idx)}
              aria-current={idx === i}
              aria-label={`Show ${g.graha}`}
              className="graha-dot"
              data-testid={`${testId}-dot-${g.slug}`}
            >
              <span className="graha-dot__deva">{g.deva}</span>
              <span>{g.graha}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 text-[12px]">
          <Link
            to={`/graha/${active.slug}`}
            className="ui inline-flex items-center gap-1.5 font-medium tracking-[0.14em] uppercase text-gold hover:underline"
            data-testid={`${testId}-primary-link`}
          >
            Open {active.graha}'s page <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <span className="text-dim">or</span>
          <Link
            to="/shop/gemstones"
            className="ui text-dim underline underline-offset-4 hover:text-ink"
          >
            see every planet's stone
          </Link>
        </div>
      </div>
    </div>
  );
}
